/**
 * The optional Category field on the list forms (20260813f): tagging a
 * list with a site category puts it in that category's search results
 * and on its page — the owner's way of publishing "Top 16 Burgers in
 * Los Angeles" into Burgers.
 *
 * A datalist keeps the 2,000-row catalog usable on an iPad: type to
 * filter, pick from the native suggestions. The input carries the
 * category NAME; resolve it back to an id on save with
 * resolveCategoryId. Same can_tag rule as yoink tagging — structural
 * Sets are filed through their children.
 */
import { escapeHtml } from '../../../assets/js/utils.js';
import { fetchAllCategories } from '../../core/categories.js';
import { listsCategoryAvailable } from './lists.js';

/**
 * The taggable catalog, or null when the field shouldn't render — the
 * column isn't applied yet (a shown field would promise a save that
 * 400s) or the catalog can't be read.
 */
export async function loadCategoryField() {
  if (!(await listsCategoryAvailable())) return null;
  try {
    const cats = (await fetchAllCategories()).filter((c) => c.can_tag !== false);
    return cats.length ? cats : null;
  } catch {
    return null;
  }
}

/** The field's markup; '' when cats is null. idSuffix keeps the two
 *  forms' datalist ids from colliding if both are ever in the DOM. */
export function categoryFieldHtml(cats, currentId = null, idSuffix = '') {
  if (!cats) return '';
  const current = currentId ? cats.find((c) => c.id === currentId) : null;
  const listId = `list-category-options${idSuffix}`;
  return `
    <label class="mylist-vis-label">Category (optional)
      <input type="text" name="category" list="${listId}" value="${escapeHtml(current?.name || '')}"
        placeholder="e.g. Burgers — shows the list on that category" autocomplete="off" maxlength="80">
    </label>
    <datalist id="${listId}">
      ${cats.map((c) => `<option value="${escapeHtml(c.name)}"></option>`).join('')}
    </datalist>`;
}

/**
 * Typed text → category id. Exact name match, case-insensitive; empty
 * means "no category" (null). Text that matches nothing returns
 * undefined so the caller can say so instead of silently dropping what
 * the user typed.
 */
export function resolveCategoryId(cats, raw) {
  const name = (raw || '').trim().toLowerCase();
  if (!name) return null;
  const match = (cats || []).find((c) => (c.name || '').toLowerCase() === name);
  return match ? match.id : undefined;
}
